
import { Vue, Component, Watch } from 'vue-property-decorator'
import Order from '@/types/order'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import PageHeader from '@/components/base/PageHeader.vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SearchableColumnFilter, PageHeader, ActiveFilters }
})

export default class Orders extends Vue {
  settings = {
    routeNames: { create: 'orderCreate', show: 'orderShow', edit: 'orderEdit' },
    routeParamKey: 'orderId',
    permissionNames: { show: 'orders.view', edit: 'orders.edit', delete: 'orders.delete' },
    functionNames: { getList: 'getOrderList' },
    getterNames: { list: 'orderList', loading: 'loadingOrder' },
    localStorageName: 'orderTableSettings'
  }

  columns:Column[] = [
    {
      title: '#',
      key: 'id',
      dataIndex: 'id',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project.name',
      filters: [],
      scopedSlots: { filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bouwnummer',
      key: 'building.number',
      dataIndex: 'building.number',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Orderwaarde',
      key: 'total',
      scopedSlots: { customRender: 'total' }
    },
    {
      title: 'Klant',
      key: 'customer.reference',
      dataIndex: 'customer.reference',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Geplaatst door',
      key: 'user.full_name',
      dataIndex: 'user.full_name',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Geplaatst op',
      key: 'created_at',
      dataIndex: 'created_at',
      scopedSlots: { customRender: 'created_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Fase',
      key: 'phase',
      dataIndex: 'phase_text',
      filters: [
        {
          text: 'Modulair',
          value: 0
        },
        {
          text: 'Ruwbouw',
          value: 1
        },
        {
          text: 'Afbouw',
          value: 2
        },
        {
          text: 'Extra',
          value: 5
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status_text',
      filters: [
        {
          text: 'Nieuw',
          value: 1
        },
        {
          text: 'In behandeling',
          value: 2
        },
        {
          text: 'Verwerkt',
          value: 3
        },
        {
          text: 'Verwijderd',
          value: 4
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  tableSettings:TableSettings = ls.get(this.settings.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: null,
    sort: {
      columnKey: 'id',
      field: 'id',
      order: 'descend'
    },
    search: null
  }

  filtersLoaded = false

  mounted () : void {
    this.reload()
    this.getFilters()
    this.onTableSettingsChange()
  }

  reload () :void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  async getFilters () : Promise<void> {
    await this.getProjectFilter()
    this.filtersLoaded = true
  }

  getProjectFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/project/orders', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns[1].filters = res.data
    })
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.reload()
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.reload()
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}, archived:boolean, unrelated:boolean) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = filters
    this.tableSettings.archived = archived
    this.tableSettings.unrelated = unrelated
    this.reload()
  }

  filterVisible = false
  filterDropdownVisibleChange (visible:boolean) : void {
    this.filterVisible = visible
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          const keyboard = window.event as KeyboardEvent
          if (can(this.settings.permissionNames.show)) {
            const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          }
        }
      }
    }
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  toggleUnrelatedItems (event: {target: { checked: boolean } }) : void {
    this.tableSettings.unrelated = event.target.checked
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  get itemList () : { data: Order[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Order ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
